/*
 * Product pages fetched for the quick add drawer, keyed by URL.
 *
 * The drawer was fetching on click, so every open paid the full round trip. The
 * fetch now starts as soon as someone shows intent, hovering, focusing or
 * touching a Choose options button, which is typically a few hundred
 * milliseconds before the click and usually enough for the response to be ready
 * by the time the drawer opens. A small number of cards are also warmed while
 * the browser is idle after load.
 *
 * Values are promises, so a click during an in-flight prefetch waits on the same
 * request rather than starting a second one.
 */
const vmQuickAddCache = new Map();

/* Every product template names its product section 'main', so the section
   rendering API can return that section alone instead of the whole page. It is a
   fraction of the bytes: no head, no header, no footer, no related products, no
   app embeds. */
function vmQuickAddSectionUrl(url) {
  return url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=main';
}

function vmQuickAddPrefetch(url) {
  if (!url || vmQuickAddCache.has(url)) return vmQuickAddCache.get(url);

  const request = fetch(vmQuickAddSectionUrl(url))
    .then((response) => response.text())
    .catch((error) => {
      // A failed prefetch must not poison the cache for the real click.
      vmQuickAddCache.delete(url);
      throw error;
    });

  vmQuickAddCache.set(url, request);
  return request;
}

(function () {
  function urlFrom(target) {
    const opener = target && target.closest ? target.closest('[data-product-url]') : null;
    return opener ? opener.getAttribute('data-product-url') : null;
  }

  ['mouseover', 'touchstart', 'focusin'].forEach((event) => {
    document.addEventListener(
      event,
      (e) => {
        const url = urlFrom(e.target);
        if (url) vmQuickAddPrefetch(url);
      },
      { passive: true, capture: true }
    );
  });

  // Warm a handful of the cards actually on screen, once the browser is idle.
  function warm() {
    const buttons = document.querySelectorAll('.quick-add [data-product-url]');
    let warmed = 0;

    for (let i = 0; i < buttons.length && warmed < 4; i++) {
      const box = buttons[i].getBoundingClientRect();
      if (box.top < window.innerHeight && box.bottom > 0) {
        vmQuickAddPrefetch(buttons[i].getAttribute('data-product-url'));
        warmed++;
      }
    }
  }

  if (window.requestIdleCallback) {
    window.requestIdleCallback(warm, { timeout: 3000 });
  } else {
    window.addEventListener('load', () => setTimeout(warm, 1200));
  }
})();

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        document.querySelectorAll('[data-vm-consent][data-quick-add]').forEach((el) => el.remove());
        this.querySelectorAll('.qa-sticky-cta, .qa-footer').forEach((el) => el.remove());

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      skeleton() {
        return (
          '<div class="qa-skeleton">' +
          '<div class="qa-skeleton__line qa-skeleton__line--title"></div>' +
          '<div class="qa-skeleton__line qa-skeleton__line--short"></div>' +
          '<div class="qa-skeleton__block"></div>' +
          '<div class="qa-skeleton__line"></div>' +
          '<div class="qa-skeleton__line qa-skeleton__line--short"></div>' +
          '<div class="qa-skeleton__block qa-skeleton__block--small"></div>' +
          '</div>'
        );
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // The rating is lifted from the card that opened the drawer. It is already
        // rendered from the reviews metafield, so it needs no app script to run
        // inside the drawer, and it is the same stars the buyer just looked at.
        const card = opener.closest('.card-wrapper, .grid__item, li');
        const cardRating = card ? card.querySelector('.card__content .rating, .rating') : null;
        const cardCount = card ? card.querySelector('.rating-count') : null;
        this.cardRatingHTML = cardRating
          ? '<div class="qa-compact__rating">' +
            cardRating.outerHTML +
            (cardCount ? cardCount.outerHTML : '') +
            '</div>'
          : '';

        // Open straight away with a placeholder. Waiting for the fetch before
        // opening made the button feel unresponsive, since nothing happens on
        // screen until the whole product page has come back.
        this.modalContent.innerHTML = this.skeleton();
        this.classList.add('is-loading');
        super.show(opener);

        const productUrl = opener.getAttribute('data-product-url');

        vmQuickAddPrefetch(productUrl)
          .then((responseText) => {
            // A section-only response that does not contain the product section
            // means the section render did not work for this template. Fall back
            // to the full page rather than giving up.
            const probe = new DOMParser().parseFromString(responseText, 'text/html');
            if (probe.querySelector('section[id^="MainProduct-"]')) return responseText;

            vmQuickAddCache.delete(productUrl);
            return fetch(productUrl).then((response) => response.text());
          })
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.buildCompactLayout(responseHTML);
            this.setInnerHTML(
              this.modalContent,
              this.compactHeader + this.productElement.innerHTML + this.compactConsent
            );

            this.classList.remove('is-loading');
            this.relocateConsent();
            if (window.vmSyncDrawerOffers) window.vmSyncDrawerOffers();

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            // this.updateImageSizes();
            this.preventVariantURLSwitching();
            this.buildFooter();
            this.loadConsent(productUrl);

            // After the footer is built, not before. The wallet button is a web
            // component wrapping an iframe, and moving the form into the footer
            // re-parents that iframe, which blanks a button that has already
            // been initialised.
            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
          })
          .catch((error) => {
            this.classList.remove('is-loading');
            // Only a genuinely empty drawer is worth leaving the page for. A
            // failure after the content is in place, in the footer or the offer
            // sync, must not throw the buyer onto the product page.
            if (!this.modalContent.querySelector('.product_content')) {
              window.location = productUrl;
              return;
            }
            console.error('Quick add:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      /*
       * Trims the injected product section down to a drawer.
       *
       * The gallery column goes entirely, replaced by one small image in a header
       * built here. When the product page uses Tab blocks, everything outside a tab
       * is dropped too, so the drawer shows only the tab strip and its panels. That
       * makes the tabs the single thing controlling what appears here: drag a block
       * into Sale or Regular on the product page and it shows up in the drawer.
       *
       * A product with no Tab blocks keeps the old behaviour, so nothing breaks on
       * templates that have not been converted.
       */
      buildCompactLayout(responseHTML) {
        this.compactHeader = '';
        this.compactConsent = '';

        /* The consent agreement is its own section, so it is not part of the
           product section being injected. If the product page has one, it comes
           along, otherwise a product needing a signature would be buyable from
           the drawer with nothing signed. vm-consent.js is loaded site wide from
           theme.liquid, so the form works as soon as it is in the DOM. */
        const consent = responseHTML.querySelector('[data-vm-consent]');
        if (consent) {
          consent.setAttribute('hidden', '');
          this.compactConsent = consent.outerHTML;
        }


        if (window.vmConsent) window.vmConsent.reset();

        const media = this.productElement.querySelector('.product_media');
        const title = responseHTML.querySelector('.product_title');
        const image = responseHTML.querySelector('.product_media--img, .product_images img');

        const src = image ? image.getAttribute('src') : '';
        const heading = title ? title.textContent.trim() : '';

        // Lets the stylesheet hide the section's own rating, but only when the
        // header is already showing one.
        this.modalContent.classList.toggle('qa-has-rating', !!this.cardRatingHTML);

        if (heading) {
          // The image is in the markup on every screen and hidden with CSS on
          // mobile, so the two layouts stay one component.
          this.compactHeader =
            '<div class="qa-compact__head">' +
            (src ? '<img class="qa-compact__img" src="' + src + '" alt="" loading="lazy">' : '') +
            '<div class="qa-compact__meta">' +
            '<p class="qa-compact__title">' + heading + '</p>' +
            (this.cardRatingHTML || '') +
            '</div>' +
            '</div>';
        }

        if (media) media.remove();

        if (this.productElement.querySelector('.vm-pdp-tabs')) {
          this.productElement
            .querySelectorAll('.vm-tab-item[data-tab="all"]')
            .forEach((item) => item.remove());
          this.modalContent.classList.add('quick-add-modal__content-info--tabs');
        } else {
          this.modalContent.classList.remove('quick-add-modal__content-info--tabs');
        }
      }

      /* The agreement renders as a fixed full screen overlay. Left inside the
         drawer it would be at the mercy of the drawer's own stacking and
         overflow, so it is moved to body where nothing can clip it. */
      /* The agreement is a separate section, so a section-only fetch cannot carry
         it. The full page is pulled in the background once the drawer is already
         open and usable; the form is only needed at the moment someone presses
         add to cart, which is always later than this resolves. */
      loadConsent(url) {
        if (!url) return;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            if (!this.hasAttribute('open')) return;

            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const consent = parsed.querySelector('[data-vm-consent]');
            if (!consent) return;

            consent.setAttribute('hidden', '');
            consent.setAttribute('data-quick-add', '');
            document.querySelectorAll('[data-vm-consent][data-quick-add]').forEach((el) => el.remove());
            document.body.appendChild(consent);
          })
          .catch(() => {});
      }

      relocateConsent() {
        document.querySelectorAll('[data-vm-consent][data-quick-add]').forEach((old) => old.remove());

        const consent = this.modalContent.querySelector('[data-vm-consent]');
        if (!consent) return;

        consent.setAttribute('data-quick-add', '');
        document.body.appendChild(consent);
      }

      /*
       * Moves the buying controls out of the scrolling area into a footer.
       *
       * Sticky was the wrong tool: it lifts an element out of the card it belongs
       * to and floats it over content. A real footer sits below the scroll area,
       * so quantity and add to cart share a line, the wallet buttons sit under
       * them, and nothing overlaps.
       *
       * The consent tick boxes go the other way, up into the scrolling content,
       * because the prescription text runs several lines and would otherwise make
       * the footer take a third of the sheet.
       */
      buildFooter() {
        const form = this.modalContent.querySelector('.product_form');
        if (!form) return;

        // Which tab the form belonged to, read before it is moved. With no tabs
        // this is 'all' and the footer is simply always shown, which is the
        // default add to cart behaviour.
        const holder = form.closest('.vm-tab-item');
        const ownerTab = holder ? holder.getAttribute('data-tab') : 'all';

        /* The tick boxes come out of the form so they scroll instead of riding in
           the footer, but they have to land back inside the form's own tab.
           Dropped into .product_content they sit outside every tab and show on
           all of them, so the Sale tab ended up with the regular purchase's
           consent underneath each offer's own. */
        const termsHome =
          holder || this.modalContent.querySelector('.product_content') || this.modalContent;
        form.querySelectorAll('.terms-container').forEach((terms) => termsHome.appendChild(terms));

        // The quantity input keeps its form attribute, so it stays bound to the
        // product form wherever it sits in the DOM.
        const actions = form.querySelector('.addtocart--wrp_wrp');
        const quantity = this.modalContent.querySelector('.product_quantity');
        if (actions && quantity) actions.insertBefore(quantity, actions.firstChild);

        /* The form is pinned with fixed positioning rather than moved into a
           footer element. Moving it took it out of .custom_product, and the add
           to cart handler in custom.js resolves the variant, the quantity and
           the consent boxes with $(this).closest('.custom_product'), so the
           button stopped being able to find any of them and fell through to the
           terms error. Styling it in place keeps every one of those lookups
           intact. */
        form.classList.add('qa-footer');
        form.setAttribute('data-tab', ownerTab);

        if (window.vmSyncDrawerOffers) window.vmSyncDrawerOffers();
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
